/**
 * Build/version-provenance display helpers (EP-aware).
 *
 * A run record carries the build it evaluated as flat fields:
 *   { evaluationManifestId, manifestBuild: 'Build_V22 draft',
 *     releaseActive: boolean, releaseAction: 'DELETED' | ... }
 * `manifestBuild` already carries the "draft" nomenclature for the lowest env,
 * so it is rendered verbatim.
 */

export type BuildStatus = 'active' | 'inactive' | 'deleted';

/** Badge treatment for a build status. `error` flags a build that no longer exists. */
export type BuildVariant = 'active' | 'inactive' | 'error';

export interface BuildProvenance {
  manifestBuild?: string;
  releaseActive?: boolean;
  releaseAction?: string;
}

// Resolves the build status shown as the suffix. DELETED wins over the active flag.
export function getBuildStatus({
  releaseActive,
  releaseAction,
}: BuildProvenance = {}): BuildStatus {
  if (releaseAction === 'DELETED') return 'deleted';
  return releaseActive ? 'active' : 'inactive';
}

// Maps a run's release fields to a badge variant.
export function getBuildVariant(run: BuildProvenance = {}): BuildVariant {
  const status = getBuildStatus(run);
  return status === 'deleted' ? 'error' : status;
}

// "<manifestBuild> (<status>)", e.g. "Build_V22 draft (active)". Empty when
// there is no build (non-EP bots / legacy runs).
export function formatBuild(
  manifestBuild: string | undefined | null,
  status: BuildStatus | string
): string {
  return manifestBuild ? `${manifestBuild} (${status})` : '';
}

/**
 * "{envLabel} · {manifestBuild (status)}". Falls back to just the env label
 * when no build is present.
 */
export function formatEnvWithBuild(
  envLabel: string,
  manifestBuild: string | undefined | null,
  status: BuildStatus | string
): string {
  const buildStr = formatBuild(manifestBuild, status);
  return buildStr ? `${envLabel} · ${buildStr}` : envLabel || '';
}
